import React from 'react'
import styled, { keyframes } from 'styled-components'

import { BREAKPOINT } from '../../../design/breakpoint.constant'
import { COLOR } from '../../design/color/color.constants'
import { SPACING } from '../../design/spacing/spacing.constants'
import { FONT_SIZE, DEFAULT_FONT_STACK, FONT_REGULAR } from '../../design/typography/typography.constants'
import { Body, SmallText } from '../../design/typography/typography.styles'
import { Loader } from '../loader/loader.component'

const InputOnFocusStyles = `border-color: ${COLOR.BLUE_MEDIUM};`

const InputStyles = props => `
  box-sizing: border-box;
  flex-grow: 1;
  ${FONT_REGULAR};
  height: 40px;
  padding: 0 ${SPACING.S};
  color: ${props.disabled ? COLOR.GREY_DARK : COLOR.BLACK};
  border: 1px solid;
  border-color: ${props.error ? COLOR.RED_MEDIUM : COLOR.GREY_LIGHT};
  border-radius: 4px;
  outline: none;
  background-color: ${props.disabled ? COLOR.GREY_LIGHTEST : COLOR.WHITE};
  ${props.isFocused ? InputOnFocusStyles : ''}

  // Overrides GGB global styling
  && {
    font-size: ${FONT_SIZE.MEDIUM};
  }

  &:focus {
    ${InputOnFocusStyles}
  }

  &::placeholder {
    color: ${COLOR.GREY_DARK};
  }

  ${props.showClearIcon ? `padding-right: ${SPACING.XL}` : ''}
  ${props.showSuffix && !props.showClearIcon
    ? 'border-radius: 4px 0 0 4px;'
    : ''
  }
`

const StyledError = styled(Body)`
  visibility: ${props => props.error ? 'visible' : 'hidden' };
  height: 16px;
  @media screen and (min-width: ${BREAKPOINT.TABLET}) {
    height: 32px;
  }
  margin: 8px 0;
  color: ${COLOR.RED_MEDIUM};
`


const StyledMenu = styled(({
  className,
  children,
  innerProps,
}) => <div
        className={className}
        data-testId="autocomplete-menu"
        {...innerProps}
      >
        {children}
      </div>
)`
  position: absolute;
  display: ${props => (props.selectProps.options.length === 0 ? 'none' : 'block')};
  width: ${props => (typeof props.selectProps.menuWidth === 'string' ? props.selectProps.menuWidth : '100%')};
  background: white;
  border: 1px solid ${COLOR.GREY_LIGHT};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  z-index: 1;
  ${({ selectProps }) => (typeof selectProps.maxHeight === 'number'
    ? `
      max-height: ${selectProps.maxHeight}px;
      overflow-y: scroll;
      `
    : ''
  )}
`

const OptionHoverContainer = styled.div`
  margin: 0 -${SPACING.S};
  padding: ${SPACING.XS} ${SPACING.S};
  background-color: ${props => ((props.isSelected || props.isFocused) ? COLOR.GREY_LIGHTER : 'transparent')};

  &:hover {
    background-color: ${COLOR.GREY_LIGHTER};
  }
`

const StyledOption = styled((props) => {
  const {
    className,
    children,
    data,
    selectProps,
    innerProps,
    options,
    isFocused,
  } = props

  const optionRef = React.useRef(null)
  const { groupSettings } = selectProps
  const isGrouped = groupSettings && groupSettings.length
  const currentGroup = isGrouped &&
    options.find(group => group.options && group.options.find(option => option === data))

  const GroupedCustomOptionComponent = currentGroup && currentGroup.customOptionComponent
  const CustomOptionComponent = GroupedCustomOptionComponent || selectProps.customOptionComponent

  if (isFocused && optionRef.current) {
    optionRef.current.scrollIntoView({ block: 'nearest' })
  }

  return (
    <div
      className={className}
      data-testId="autocomplete-option"
      {...innerProps}
      ref={optionRef}
    >
      <OptionHoverContainer isFocused={props.isFocused}>
        {
          CustomOptionComponent
            ? <CustomOptionComponent data={data} />
            : <Body>{data[selectProps.displayKey] || children}</Body>
        }
      </OptionHoverContainer>
    </div>
  )
})`
  overflow: show;
  position: relative;
  margin: 0 ${SPACING.S};
  outline: none;

  &:not(:first-child) {
    padding-top: ${props => (
      props.selectProps.showOptionSeparators
        ? SPACING.XS
        : 0
    )};
  }

  &:not(:last-child) {
    padding-bottom: ${props => (
      props.selectProps.showOptionSeparators
        ? SPACING.XS
        : 0
    )};

    border-bottom: ${props => (
      props.selectProps.showOptionSeparators
        ? `1px solid ${COLOR.GREY_LIGHT}`
        : 0
    )};
  }

  ${OptionHoverContainer} > ${Body} {
    margin: 0;
  }
`

const StyledControl = styled(({
  className,
  innerProps,
  children,
  isFocused,
}) => <div
        className={className}
        onMouseDown={innerProps.onMouseDown}
        onTouchEnd={innerProps.onTouchEnd}
        data-testId="autocomplete-wrapper"
        data-focus={isFocused}
      >
        {children}
      </div>
)(props => InputStyles({
    error: props.selectProps.error,
    ...props,
  }) + `
    display: flex;
    flex-direction: row;
    padding: 0 10px 0 0;
  `
)

const StyledValueContainer = styled(({
  className,
  children,
}) => <div
        className={className}
        data-testId="autocomplete-text-wrapper"
      >
        {children}
      </div>
)`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0 ${SPACING.S};
  cursor: text;
  overflow: hidden;
  cursor: pointer;
`

const blinkingCaret = keyframes`
  0% { opacity: 0; }
  50% { opacity: 0; }
  60% { opacity: 1; }
  100% { opacity: 1; }
`

const StyledSingleValue = styled(({
  className,
  children,
  selectProps,
  data
}) => <div
        className={className}
        data-testId="autocomplete-text"
      >
        {data[selectProps.displayKey] || children}
      </div>
)`
  max-width: 100%;
  height: 1.3em;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  &:after {
    content: '';
    display: inline-block;
    vertical-align: middle;
    position: relative;
    width: 1px;
    height: 20px;
    background-color: ${COLOR.BLACK};
    opacity: 0;
  }

  ${StyledControl}[data-focus='true'] &:after {
    animation: ${blinkingCaret} 0.5s linear infinite alternate;
  }
`

const StyledPlaceholder = styled(({
  className,
  selectProps,
}) => <div
        className={className}
        data-testId="autocomplete-placeholder"
      >
        {selectProps.placeholder}
      </div>
)`
  box-sizing: border-box;
  position: absolute;
  width: 100%;
  top: 50%;
  left: 0;
  color: ${COLOR.GREY_DARK};
  line-height: 1em;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 ${SPACING.S};
  transform: translateY(-50%);
  overflow: hidden;
`

const StyledInput = styled(({
  className,
  children,
  cx,
  autoComplete,
  innerRef,
  isDisabled,
  isHidden,
  theme,
  getStyles,
  ...rest
}) => <div
        className={className}
        data-testId="autocomplete-hidden-input"
      >
        <input
          disabled={isDisabled}
          autoComplete="off"
          {...rest}
        />
      </div>
)`
  width: 100%;
  height: 1.3em;
  font-size: inherit;
  color: inherit;
  background: transparent;
  padding: 0;
  border: 0;
  outline: none;

  ${StyledSingleValue}:not(:empty) ~ & {
    box-sizing: border-box;
    position: absolute;
    height: 100%;
    top: 0;
    left: 0;

    input {
      height: 100%;
      color: transparent;
      text-shadow: 0 0 0 ${COLOR.BLACK};
      padding: 0 16px;
    }
  }

  input {
    position: relative;
    width: 100%;
    height: 1.13em;
    color: ${COLOR.BLACK};
    font-size: ${FONT_SIZE.MEDIUM};
    ${DEFAULT_FONT_STACK}
    background: transparent;
    padding: 0;
    border: 0;
    outline: none;
  }
`

const StyledGroupTitle = styled(SmallText)`
  padding: 0 ${SPACING.S};
  margin: 0 0 ${SPACING.XXS} 0;
`

const StyledGroup = styled(({
  children,
  className,
  label,
}) => (
  <div className={className}>
    <StyledGroupTitle>{label}</StyledGroupTitle>
    {children}
  </div>
))`
  box-sizing: border-box;
  padding: 0;

  :not(:last-of-type) {
    &:after {
      content: "";
      display: block;
      margin: ${SPACING.XS} ${SPACING.S} ${SPACING.S};
      height: 1px;
      background: ${COLOR.GREY_LIGHT};
    }
  }
`

const NoOptionsMessage = () => null

const ClearIndicator = () => null

const DropdownIndicator = () => null

const IndicatorSeparator = () => null

const StyledLoadingIndicator = () => <Loader />

export {
  StyledMenu,
  StyledOption,
  StyledControl,
  StyledValueContainer,
  StyledSingleValue,
  StyledPlaceholder,
  StyledInput,
  StyledGroup,
  NoOptionsMessage,
  ClearIndicator,
  DropdownIndicator,
  IndicatorSeparator,
  StyledLoadingIndicator,
  StyledError,
}
